import type {
  NotificationCategoryKey,
  WebPushDevice,
  WebPushDeviceListResponse,
} from '../client';

export type WebPushSetupState =
  | 'unavailable'
  | 'unsupported'
  | 'installation-required'
  | 'permission-required'
  | 'enabled'
  | 'blocked'
  | 'repair-required';

export type WebPushReconcileReason =
  | 'startup'
  | 'user-change'
  | 'focus'
  | 'visible'
  | 'modal-open'
  | 'repair'
  | 'subscription-change';

interface SubscriptionMaterial {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

interface PushSubscriptionJson {
  endpoint?: string;
  keys?: { p256dh?: string; auth?: string };
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

interface WebPushConfig {
  enabled: boolean;
  publicKey?: string;
}

interface WebPushDeviceState {
  subscriptionId: number | null;
  optedOut: boolean;
  deviceName: string;
}

interface Operation {
  userId: string;
  generation: number;
  controller: AbortController;
  reason?: WebPushReconcileReason;
}

type CoordinationMessage = {
  type: 'devices-changed';
  userId: string;
};

const SERVICE_WORKER_URL = '/service-worker.js';
const SERVICE_WORKER_SCOPE = '/';
const CHANNEL_NAME = 'web-push-lifecycle';
const LOGOUT_TIMEOUT_MS = 1_500;

let currentUserId: string | null = null;
let lifecycleGeneration = 0;
const reconciliationByUser = new Map<string, Promise<void>>();
const activeControllers = new Set<AbortController>();
const activeOperationsByUser = new Map<string, Set<Operation>>();
const memoryDeviceStateByUser = new Map<string, WebPushDeviceState>();

function bounded(value: string, maximum: number, fallback: string): string {
  const normalized = value.replace(/\s+/gu, ' ').trim();
  let result = '';
  for (const codePoint of normalized || fallback) {
    if (result.length + codePoint.length > maximum) break;
    result += codePoint;
  }
  return result;
}

function browserName(): string {
  const ua = navigator.userAgent;
  if (/Edg(?:A|iOS)?\//u.test(ua)) return 'Microsoft Edge';
  if (/Firefox\//u.test(ua)) return 'Firefox';
  if (/CriOS\//u.test(ua)) return 'Chrome';
  if (/FxiOS\//u.test(ua)) return 'Firefox';
  if (/Chrome\//u.test(ua)) return 'Chrome';
  if (/Safari\//u.test(ua)) return 'Safari';
  return 'Web browser';
}

function platformName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/u.test(ua)) return 'iPhone';
  if (/iPad/u.test(ua) || isDesktopModeIPad()) return 'iPad';
  if (/Android/u.test(ua)) return 'Android';
  if (/Mac/u.test(navigator.platform || ua)) return 'macOS';
  if (/Win/u.test(navigator.platform || ua)) return 'Windows';
  if (/Linux/u.test(navigator.platform || ua)) return 'Linux';
  return bounded(navigator.platform || '', 40, 'Unknown platform');
}

export function suggestedWebPushDeviceName(): string {
  const browser = browserName();
  const platform = platformName();
  if (browser === 'Web browser' || platform === 'Unknown platform') {
    return 'This device';
  }
  return `${browser} on ${platform}`;
}

function isDesktopModeIPad(): boolean {
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export function isAppleMobileDevice(): boolean {
  return /iPad|iPhone|iPod/u.test(navigator.userAgent) || isDesktopModeIPad();
}

export function isStandaloneWebApp(): boolean {
  return Boolean(
    (navigator as NavigatorWithStandalone).standalone ||
      window.matchMedia?.('(display-mode: standalone)').matches,
  );
}

function supported(): boolean {
  return Boolean(
    typeof window.Notification !== 'undefined' &&
      typeof window.PushManager !== 'undefined' &&
      navigator.serviceWorker,
  );
}

function base64UrlBytes(value: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]{87}$/u.test(value)) return null;
  try {
    const padded = `${value.replace(/-/gu, '+').replace(/_/gu, '/')}${'='.repeat(
      (4 - (value.length % 4)) % 4,
    )}`;
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    return bytes.length === 65 && bytes[0] === 4 ? bytes : null;
  } catch {
    return null;
  }
}

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

function checkSignal(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortError();
}

function awaitAbortable<T>(
  promise: Promise<T>,
  signal: AbortSignal,
): Promise<T> {
  checkSignal(signal);
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        if (signal.aborted) reject(abortError());
        else resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
}

async function jsonFetch<T>(
  url: string,
  init: RequestInit = {},
  signal?: AbortSignal,
): Promise<T> {
  checkSignal(signal);
  const response = await fetch(url, {
    credentials: 'include',
    redirect: 'error',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
    ...init,
    signal,
  });
  checkSignal(signal);
  if (!response.ok) {
    let details: { code?: unknown } | null = null;
    try {
      details = (await response.json()) as { code?: unknown };
      checkSignal(signal);
    } catch (error) {
      checkSignal(signal);
      if ((error as { name?: string })?.name === 'AbortError') throw error;
    }
    const error = new Error(`Web Push request failed (${response.status})`);
    Object.assign(error, {
      status: response.status,
      retryAfter: response.headers.get('Retry-After'),
      code: typeof details?.code === 'string' ? details.code : undefined,
    });
    throw error;
  }
  const value = (await response.json()) as T;
  checkSignal(signal);
  return value;
}

async function config(
  signal?: AbortSignal,
): Promise<{ ok: true; value: WebPushConfig } | { ok: false }> {
  try {
    return {
      ok: true,
      value: await jsonFetch<WebPushConfig>(
        '/api/notifications/push/config',
        {},
        signal,
      ),
    };
  } catch (error) {
    if (
      signal?.aborted ||
      (error as { name?: string })?.name === 'AbortError'
    ) {
      throw error;
    }
    return { ok: false };
  }
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register(SERVICE_WORKER_URL, {
    scope: SERVICE_WORKER_SCOPE,
  });
}

async function existingRegistration(): Promise<
  ServiceWorkerRegistration | undefined
> {
  return navigator.serviceWorker.getRegistration(SERVICE_WORKER_SCOPE);
}

function hasPushManager(worker: ServiceWorkerRegistration): boolean {
  return Boolean(
    worker.pushManager &&
      typeof worker.pushManager.getSubscription === 'function' &&
      typeof worker.pushManager.subscribe === 'function',
  );
}

function materialFromSubscription(
  subscription: PushSubscription,
): SubscriptionMaterial | null {
  const value = subscription.toJSON() as PushSubscriptionJson;
  if (
    typeof value.endpoint !== 'string' ||
    typeof value.keys?.p256dh !== 'string' ||
    typeof value.keys.auth !== 'string'
  ) {
    return null;
  }
  return {
    endpoint: value.endpoint,
    keys: { p256dh: value.keys.p256dh, auth: value.keys.auth },
  };
}

function bytes(value: BufferSource): Uint8Array {
  return value instanceof ArrayBuffer
    ? new Uint8Array(value)
    : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
}

function subscriptionUsesKey(
  subscription: PushSubscription,
  expected: Uint8Array,
): boolean {
  const configured = subscription.options.applicationServerKey;
  if (!configured) return false;
  const actual = bytes(configured);
  return (
    actual.length === expected.length &&
    actual.every((value, index) => value === expected[index])
  );
}

function isExpired(subscription: PushSubscription): boolean {
  return Boolean(
    subscription.expirationTime && subscription.expirationTime <= Date.now(),
  );
}

function readDeviceState(userId: string): WebPushDeviceState {
  return (
    memoryDeviceStateByUser.get(userId) ?? {
      subscriptionId: null,
      optedOut: false,
      deviceName: 'This device',
    }
  );
}

function storeDeviceState(userId: string, state: WebPushDeviceState): void {
  memoryDeviceStateByUser.set(userId, { ...state });
}

function broadcast(message: CoordinationMessage): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(message);
    channel.close();
  } catch {
    // Cross-tab refresh is opportunistic; server reconciliation is authoritative.
  }
}

function persistAndBroadcast(userId: string, state: WebPushDeviceState): void {
  storeDeviceState(userId, state);
  broadcast({ type: 'devices-changed', userId });
}

function cancelActiveOperations(): void {
  lifecycleGeneration += 1;
  for (const controller of activeControllers) controller.abort();
  activeControllers.clear();
  activeOperationsByUser.clear();
  reconciliationByUser.clear();
}

function beginOperation(
  userId: string,
  reason?: WebPushReconcileReason,
): Operation {
  const controller = new AbortController();
  activeControllers.add(controller);
  const operation = {
    userId,
    generation: lifecycleGeneration,
    controller,
    reason,
  };
  const userOperations = activeOperationsByUser.get(userId) ?? new Set();
  userOperations.add(operation);
  activeOperationsByUser.set(userId, userOperations);
  return operation;
}

function finishOperation(operation: Operation): void {
  activeControllers.delete(operation.controller);
  const userOperations = activeOperationsByUser.get(operation.userId);
  userOperations?.delete(operation);
  if (userOperations?.size === 0) {
    activeOperationsByUser.delete(operation.userId);
  }
}

function checkOperation(operation: Operation): void {
  checkSignal(operation.controller.signal);
  if (
    operation.generation !== lifecycleGeneration ||
    currentUserId !== operation.userId
  ) {
    throw abortError();
  }
}

function checkBackgroundIntent(operation: Operation): void {
  checkOperation(operation);
  if (readDeviceState(operation.userId).optedOut) throw abortError();
}

export async function withWebPushEnrollmentLock<T>(
  userId: number,
  operation: () => Promise<T>,
): Promise<T | undefined> {
  const locks = navigator.locks;
  if (!locks?.request) return operation();
  return locks.request(
    `delegate.web-push.${userId}`,
    { ifAvailable: true },
    async (lock) => (lock ? operation() : undefined),
  );
}

async function registerOwnedSubscription(
  subscription: PushSubscription,
  deviceName: string,
  signal?: AbortSignal,
  reactivate = false,
  previousSubscriptionId: number | null = null,
): Promise<WebPushDeviceListResponse> {
  const material = materialFromSubscription(subscription);
  if (!material)
    throw new Error('The browser returned an incomplete subscription.');
  return jsonFetch<WebPushDeviceListResponse>(
    '/api/notifications/push/subscriptions',
    {
      method: 'POST',
      body: JSON.stringify({
        subscription: material,
        ...(reactivate ? { reactivate: true } : {}),
        ...(previousSubscriptionId ? { previousSubscriptionId } : {}),
        device: {
          name: bounded(deviceName, 80, 'This device'),
          browser: bounded(browserName(), 40, 'Web browser'),
          platform: bounded(platformName(), 40, 'Unknown platform'),
        },
      }),
    },
    signal,
  );
}

async function deleteOwnedSubscriptionId(
  subscriptionId: number,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await jsonFetch(
      `/api/notifications/push/subscriptions/${subscriptionId}`,
      { method: 'DELETE' },
      signal,
    );
  } catch (error) {
    if ((error as { status?: number })?.status !== 404) throw error;
  }
}

async function revokeKnownCurrentDevice(
  operation: Operation,
  state: WebPushDeviceState,
): Promise<WebPushDeviceState> {
  if (!state.subscriptionId) return state;
  await deleteOwnedSubscriptionId(
    state.subscriptionId,
    operation.controller.signal,
  );
  checkOperation(operation);
  const cleared = { ...state, subscriptionId: null };
  storeDeviceState(operation.userId, cleared);
  return cleared;
}

async function verifyKnownDeviceBeforeRegistration(
  operation: Operation,
  state: WebPushDeviceState,
  subscription: PushSubscription | null,
): Promise<boolean> {
  if (!state.subscriptionId) return true;
  let response: WebPushDeviceListResponse;
  try {
    response = await jsonFetch<WebPushDeviceListResponse>(
      '/api/notifications/push/subscriptions',
      {},
      operation.controller.signal,
    );
  } catch {
    checkOperation(operation);
    return false;
  }
  checkBackgroundIntent(operation);
  if (!Array.isArray(response.devices)) return false;
  if (response.devices.some((device) => device.id === state.subscriptionId)) {
    return true;
  }
  persistAndBroadcast(operation.userId, {
    ...state,
    subscriptionId: null,
    optedOut: true,
  });
  await subscription?.unsubscribe().catch(() => false);
  return false;
}

export function setCurrentWebPushUser(userId: string | number | null): void {
  const next = userId === null ? null : String(userId);
  if (next === currentUserId) return;
  const previous = currentUserId;
  cancelActiveOperations();
  currentUserId = next;
  if (!next && previous) memoryDeviceStateByUser.clear();
}

export async function getWebPushSetupState(): Promise<WebPushSetupState> {
  const userState = currentUserId
    ? readDeviceState(currentUserId)
    : { subscriptionId: null, optedOut: false, deviceName: 'This device' };
  const publicConfig = await config();
  if (!publicConfig.ok) {
    return userState.optedOut ? 'permission-required' : 'repair-required';
  }
  const applicationServerKey = base64UrlBytes(
    publicConfig.value.publicKey || '',
  );
  if (!publicConfig.value.enabled || !applicationServerKey)
    return 'unavailable';
  if (isAppleMobileDevice() && !isStandaloneWebApp()) {
    return 'installation-required';
  }
  if (!supported()) return 'unsupported';
  if (window.Notification.permission === 'denied') return 'blocked';
  if (userState.optedOut) return 'permission-required';
  let worker: ServiceWorkerRegistration | undefined;
  try {
    worker = await existingRegistration();
  } catch {
    return 'repair-required';
  }
  if (!worker) return 'permission-required';
  if (!hasPushManager(worker)) return 'unsupported';
  if (window.Notification.permission !== 'granted') {
    return 'permission-required';
  }
  try {
    const subscription = await worker.pushManager.getSubscription();
    if (
      !subscription ||
      isExpired(subscription) ||
      !subscriptionUsesKey(subscription, applicationServerKey)
    ) {
      return 'repair-required';
    }
    return 'enabled';
  } catch {
    return 'repair-required';
  }
}

async function reconcileOwnedSubscription(operation: Operation): Promise<void> {
  checkOperation(operation);
  let state = readDeviceState(operation.userId);
  if (state.optedOut) return;

  if (!supported()) return;
  const worker = await existingRegistration();
  if (!worker) return;
  checkOperation(operation);
  if (!hasPushManager(worker)) return;
  let subscription = await worker.pushManager.getSubscription();
  checkOperation(operation);

  // A missing subscription means this browser is not enrolled. Only the
  // explicit Repair action may create one; startup/focus must not resurrect a
  // device after the user removed it.
  if (!subscription && operation.reason !== 'repair') return;

  if (window.Notification.permission === 'denied') {
    state = await revokeKnownCurrentDevice(operation, state).catch(() => state);
    checkOperation(operation);
    if (subscription) {
      await subscription.unsubscribe().catch(() => false);
      checkOperation(operation);
    }
    persistAndBroadcast(operation.userId, state);
    return;
  }
  if (window.Notification.permission !== 'granted') return;
  if (isAppleMobileDevice() && !isStandaloneWebApp()) return;

  const publicConfig = await config(operation.controller.signal);
  checkBackgroundIntent(operation);
  if (!publicConfig.ok) return;
  const applicationServerKey = base64UrlBytes(
    publicConfig.value.publicKey || '',
  );
  if (!publicConfig.value.enabled || !applicationServerKey) return;

  if (
    !(await verifyKnownDeviceBeforeRegistration(operation, state, subscription))
  ) {
    return;
  }
  const previousSubscriptionId = state.subscriptionId;

  const replace = Boolean(
    !subscription ||
      isExpired(subscription) ||
      !subscriptionUsesKey(subscription, applicationServerKey),
  );
  if (replace) {
    if (subscription) {
      await subscription.unsubscribe().catch(() => false);
      checkBackgroundIntent(operation);
    }
    const replacement = await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    try {
      checkBackgroundIntent(operation);
    } catch (error) {
      await replacement.unsubscribe().catch(() => false);
      throw error;
    }
    subscription = replacement;
  }

  try {
    checkBackgroundIntent(operation);
    const result = await registerOwnedSubscription(
      subscription!,
      state.deviceName,
      operation.controller.signal,
      false,
      previousSubscriptionId,
    );
    checkBackgroundIntent(operation);
    const next = {
      subscriptionId: result.currentSubscriptionId ?? null,
      optedOut: false,
      deviceName: state.deviceName,
    };
    persistAndBroadcast(operation.userId, next);
  } catch (error) {
    if ((error as { status?: number })?.status !== 409) throw error;
    const code = (error as { code?: string })?.code;
    if (
      code === 'WEB_PUSH_REACTIVATION_REQUIRED' ||
      code === 'WEB_PUSH_CONTINUITY_REQUIRED'
    ) {
      persistAndBroadcast(operation.userId, {
        ...state,
        subscriptionId: null,
        optedOut: true,
      });
      await subscription!.unsubscribe().catch(() => false);
      return;
    }
    checkBackgroundIntent(operation);
    await subscription!.unsubscribe().catch(() => false);
    checkBackgroundIntent(operation);
    const replacement = await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    try {
      checkBackgroundIntent(operation);
    } catch (abort) {
      await replacement.unsubscribe().catch(() => false);
      throw abort;
    }
    subscription = replacement;
    checkBackgroundIntent(operation);
    const result = await registerOwnedSubscription(
      subscription,
      state.deviceName,
      operation.controller.signal,
      false,
      previousSubscriptionId,
    );
    checkBackgroundIntent(operation);
    persistAndBroadcast(operation.userId, {
      subscriptionId: result.currentSubscriptionId ?? null,
      optedOut: false,
      deviceName: state.deviceName,
    });
  }
}

async function reconcileForUser(operation: Operation): Promise<void> {
  try {
    await withWebPushEnrollmentLock(Number(operation.userId), () =>
      reconcileOwnedSubscription(operation),
    );
  } finally {
    finishOperation(operation);
  }
}

export function reconcileWebPushSubscription(
  reason: WebPushReconcileReason,
): Promise<void> {
  const userId = currentUserId;
  if (!userId) return Promise.resolve();
  const existing = reconciliationByUser.get(userId);
  if (existing) return existing;
  const operation = beginOperation(userId, reason);
  const work = reconcileForUser(operation).finally(() => {
    if (reconciliationByUser.get(userId) === work) {
      reconciliationByUser.delete(userId);
    }
  });
  reconciliationByUser.set(userId, work);
  return work;
}

export async function enableWebPushForCurrentDevice(
  deviceName: string,
): Promise<WebPushDevice[]> {
  const userId = currentUserId;
  if (!userId) throw new Error('Log in before enabling notifications.');
  if (isAppleMobileDevice() && !isStandaloneWebApp()) {
    throw new Error('Add this app to your Home Screen first.');
  }
  if (!supported())
    throw new Error('App notifications are not supported here.');
  cancelActiveOperations();
  const operation = beginOperation(userId);
  try {
    const publicConfig = await config(operation.controller.signal);
    checkOperation(operation);
    const applicationServerKey = publicConfig.ok
      ? base64UrlBytes(publicConfig.value.publicKey || '')
      : null;
    if (
      !publicConfig.ok ||
      !publicConfig.value.enabled ||
      !applicationServerKey
    ) {
      throw new Error('App notifications are not available right now.');
    }
    let permission = window.Notification.permission;
    if (permission === 'default') {
      permission = await window.Notification.requestPermission();
      checkOperation(operation);
    }
    if (permission !== 'granted') {
      throw new Error(
        permission === 'denied'
          ? 'Notifications are blocked in this browser.'
          : 'Notification permission was not granted.',
      );
    }
    const normalizedDeviceName = bounded(deviceName, 80, 'This device');
    const worker = await registration();
    checkOperation(operation);
    if (!hasPushManager(worker)) {
      throw new Error('App notifications are not supported here.');
    }
    const state = readDeviceState(userId);
    const previousSubscriptionId = state.subscriptionId;
    let subscription = await worker.pushManager.getSubscription();
    checkOperation(operation);
    if (
      subscription &&
      (isExpired(subscription) ||
        !subscriptionUsesKey(subscription, applicationServerKey))
    ) {
      await subscription.unsubscribe().catch(() => false);
      checkOperation(operation);
      subscription = null;
    }
    if (!subscription) {
      subscription = await worker.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      checkOperation(operation);
    }
    let result: WebPushDeviceListResponse;
    try {
      result = await registerOwnedSubscription(
        subscription,
        normalizedDeviceName,
        operation.controller.signal,
        true,
        previousSubscriptionId,
      );
    } catch (error) {
      if (
        (error as { code?: string })?.code === 'WEB_PUSH_CONTINUITY_REQUIRED'
      ) {
        persistAndBroadcast(userId, {
          ...state,
          subscriptionId: null,
          optedOut: true,
        });
        await subscription.unsubscribe().catch(() => false);
      }
      throw error;
    }
    checkOperation(operation);
    persistAndBroadcast(userId, {
      subscriptionId: result.currentSubscriptionId ?? null,
      optedOut: false,
      deviceName: normalizedDeviceName,
    });
    return markCurrentWebPushDevice(result.devices);
  } finally {
    finishOperation(operation);
  }
}

function markCurrentWebPushDevice(devices: WebPushDevice[]): WebPushDevice[] {
  const userId = currentUserId;
  const state = userId
    ? readDeviceState(userId)
    : { subscriptionId: null, optedOut: false, deviceName: 'This device' };
  return devices.map((device) => ({
    ...device,
    isCurrentDevice:
      state.subscriptionId !== null && device.id === state.subscriptionId,
  }));
}

export async function listWebPushDevices(): Promise<WebPushDevice[]> {
  const response = await jsonFetch<WebPushDeviceListResponse>(
    '/api/notifications/push/subscriptions',
  );
  return markCurrentWebPushDevice(response.devices);
}

export async function updateWebPushDevicePreferences(
  id: number,
  preferences: Array<{
    category: NotificationCategoryKey;
    enabled: boolean;
  }>,
): Promise<WebPushDevice[]> {
  const response = await jsonFetch<WebPushDeviceListResponse>(
    `/api/notifications/push/subscriptions/${id}/preferences`,
    {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    },
  );
  return markCurrentWebPushDevice(response.devices);
}

async function cleanUpLocalSubscriptionBestEffort(
  operation: Operation,
): Promise<void> {
  try {
    if (!supported()) return;
    const worker = await registration();
    checkOperation(operation);
    if (!hasPushManager(worker)) return;
    const subscription = await worker.pushManager.getSubscription();
    checkOperation(operation);
    await subscription?.unsubscribe().catch(() => false);
    checkOperation(operation);
  } catch {
    // Server revocation is authoritative; local provider cleanup is best effort.
  }
}

export async function removeWebPushDevice(id: number): Promise<void> {
  const userId = currentUserId;
  if (!userId) throw new Error('Log in before removing a device.');
  const state = readDeviceState(userId);
  if (id === state.subscriptionId) cancelActiveOperations();
  const operation = beginOperation(userId);
  try {
    if (id === state.subscriptionId) {
      await deleteOwnedSubscriptionId(id, operation.controller.signal);
    } else {
      await jsonFetch(
        `/api/notifications/push/subscriptions/${id}`,
        { method: 'DELETE' },
        operation.controller.signal,
      );
    }
    checkOperation(operation);
    if (id !== state.subscriptionId) return;
    persistAndBroadcast(userId, {
      ...state,
      subscriptionId: null,
      optedOut: true,
    });
    await cleanUpLocalSubscriptionBestEffort(operation);
  } finally {
    finishOperation(operation);
  }
}

export async function removeAllWebPushDevices(): Promise<void> {
  const userId = currentUserId;
  if (!userId) throw new Error('Log in before removing devices.');
  cancelActiveOperations();
  const operation = beginOperation(userId);
  try {
    await jsonFetch(
      '/api/notifications/push/subscriptions',
      { method: 'DELETE' },
      operation.controller.signal,
    );
    checkOperation(operation);
    persistAndBroadcast(userId, {
      ...readDeviceState(userId),
      subscriptionId: null,
      optedOut: true,
    });
    await cleanUpLocalSubscriptionBestEffort(operation);
  } finally {
    finishOperation(operation);
  }
}

export async function sendWebPushTest(): Promise<void> {
  if (!supported())
    throw new Error('Enable notifications on this device first.');
  const worker = await registration();
  if (!hasPushManager(worker)) {
    throw new Error('Enable notifications on this device first.');
  }
  const subscription = await worker.pushManager.getSubscription();
  const material = subscription && materialFromSubscription(subscription);
  if (!material) throw new Error('Enable notifications on this device first.');
  await jsonFetch('/api/notifications/push/test', {
    method: 'POST',
    body: JSON.stringify({ subscription: material }),
  });
}

export async function revokeCurrentWebPushForLogout(): Promise<void> {
  const userId = currentUserId;
  if (!userId) return;
  const state = readDeviceState(userId);
  cancelActiveOperations();
  currentUserId = null;
  memoryDeviceStateByUser.clear();
  const serverController = new AbortController();
  const localController = new AbortController();
  activeControllers.add(serverController);
  activeControllers.add(localController);
  const serverTimer = window.setTimeout(
    () => serverController.abort(),
    LOGOUT_TIMEOUT_MS,
  );
  const localTimer = window.setTimeout(
    () => localController.abort(),
    LOGOUT_TIMEOUT_MS,
  );
  try {
    const serverCleanup = async () => {
      if (!state.subscriptionId) return;
      await awaitAbortable(
        deleteOwnedSubscriptionId(
          state.subscriptionId,
          serverController.signal,
        ),
        serverController.signal,
      );
      checkSignal(serverController.signal);
      storeDeviceState(userId, { ...state, subscriptionId: null });
    };
    const localCleanup = async () => {
      if (!supported()) return;
      const worker = await awaitAbortable(
        registration(),
        localController.signal,
      );
      checkSignal(localController.signal);
      if (!hasPushManager(worker)) return;
      const subscription = await awaitAbortable(
        worker.pushManager.getSubscription(),
        localController.signal,
      );
      checkSignal(localController.signal);
      if (subscription) {
        await awaitAbortable(
          subscription.unsubscribe().catch(() => false),
          localController.signal,
        );
      }
      checkSignal(localController.signal);
    };

    await Promise.allSettled([serverCleanup(), localCleanup()]);
  } finally {
    window.clearTimeout(serverTimer);
    window.clearTimeout(localTimer);
    serverController.abort();
    localController.abort();
    activeControllers.delete(serverController);
    activeControllers.delete(localController);
  }
}

export function startWebPushLifecycle(
  userId: string | number,
  reconcile: (
    reason: WebPushReconcileReason,
  ) => Promise<void> = reconcileWebPushSubscription,
): () => void {
  const normalizedUserId = String(userId);
  setCurrentWebPushUser(normalizedUserId);
  void reconcile('user-change').catch(() => undefined);
  const reconcileFocus = () => void reconcile('focus').catch(() => undefined);
  const reconcileVisible = () => {
    if (document.visibilityState === 'visible') {
      void reconcile('visible').catch(() => undefined);
    }
  };
  const reconcileSubscriptionChange = (event: MessageEvent<unknown>) => {
    const message = event.data;
    if (
      !message ||
      typeof message !== 'object' ||
      Array.isArray(message) ||
      Object.keys(message).length !== 1 ||
      (message as { type?: unknown }).type !== 'web-push-subscription-changed'
    ) {
      return;
    }
    void reconcile('subscription-change').catch(() => undefined);
  };
  let channel: BroadcastChannel | null = null;
  const notifyDeviceChange = () => {
    window.dispatchEvent(new Event('web-push-devices-changed'));
  };
  const applyMessage = (message: CoordinationMessage) => {
    if (
      !message ||
      message.userId !== normalizedUserId ||
      message.type !== 'devices-changed'
    ) {
      return;
    }
    notifyDeviceChange();
  };
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent<CoordinationMessage>) =>
        applyMessage(event.data);
    } catch {
      channel = null;
    }
  }
  window.addEventListener('focus', reconcileFocus);
  navigator.serviceWorker?.addEventListener?.(
    'message',
    reconcileSubscriptionChange,
  );
  document.addEventListener('visibilitychange', reconcileVisible);
  return () => {
    window.removeEventListener('focus', reconcileFocus);
    navigator.serviceWorker?.removeEventListener?.(
      'message',
      reconcileSubscriptionChange,
    );
    document.removeEventListener('visibilitychange', reconcileVisible);
    channel?.close();
  };
}
